/**
 * A schedule the parent sent to the tablet "now" (BA-84, BA-57).
 * Stored in Firebase at /devices/{deviceId}/status/sentSchedule; the one it displaced at
 * /devices/{deviceId}/status/previousSentSchedule. contracts/firebase-schema.md §3.2
 */
import { ScheduleType, StepData, Schedule } from './schedule';

/** 'failed': the tablet could not show it (endedBy 'error', with a reason) */
export type SentScheduleState = 'active' | 'done' | 'endedEarly' | 'failed';

/** child-done ends it as 'done'; error as 'failed'; the others as 'endedEarly' */
export type SentScheduleEndedBy = 'child-done' | 'parent-remove' | 'tablet-pin' | 'replaced' | 'error';

/** What a parent can send: a routine or a board, not a day or week plan */
export const SENDABLE_SCHEDULE_TYPES: readonly ScheduleType[] = [
  'routine', 'first_then', 'choice_board', 'waiting_board',
];

/** The schedule node as stored in RTDB (§5): steps keyed by stepId, no id field */
export type ScheduleSnapshot = Omit<Schedule, 'id' | 'steps'> & {
  steps?: Record<string, StepData> | null;
};

export interface SentStepDone {
  /** When the child finished the step (ms) */
  doneAt: number;
  skipped?: boolean;
}

export interface SentScheduleProgress {
  /** 0-based index of the step on screen, by step order */
  currentStep: number;
  /** Finished steps, keyed by stepId */
  stepsDone?: Record<string, SentStepDone>;
}

export interface SentSchedule {
  scheduleId: string;
  /** The schedule as it was when sent; a later edit does not change a running send */
  schedule: ScheduleSnapshot;
  /** When it was sent (ms, server time). With scheduleId, its identity */
  sentAt: number;
  state: SentScheduleState;
  /** Set when it ends */
  endedBy?: SentScheduleEndedBy;
  endedAt?: number;
  /** Set only when state is 'failed': a short technical reason, not for display as is */
  reason?: string;
  progress: SentScheduleProgress;
  tokensEarned: number;
}

export interface SendScheduleRequest {
  scheduleId: string;
}

export const isSentScheduleActive = (sent: SentSchedule | null | undefined): boolean =>
  sent?.state === 'active';
